import React, { useRef } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import defaultImage from './defaultImage.svg';
import './AvatarUpload.css';
import { Spinner } from '../Spinner/Spinner';
import { t } from '../../locales/translate';

interface Props extends GlobalProps {
  image?: string;
  alt?: string;
  label?: string;
  loading?: boolean;
  error?: boolean;
  shape?: 'square' | 'round';

  onChange?: (e?: File) => void;
}

export function AvatarUpload({
  image,
  alt,
  label,
  loading,
  error,
  onChange,
  className,
  style,
  shape,
}: Props): JSX.Element {
  const avatarUpload: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  function handleOnChange(file: File): void {
    if (onChange) {
      onChange(file);
      if (avatarUpload.current) {
        avatarUpload.current.value = '';
      }
    }
  }

  return (
    <div
      className={classnames(
        'avatar-upload',
        {
          'avatar-upload--loading': loading,
          'avatar-upload--round': shape === 'round',
        },
        className
      )}
      style={style}
    >
      <div className="avatar-upload__content">
        <img className="avatar-upload__image" src={image || defaultImage} alt={alt} />
        <div className="avatar-upload__hover-overlay">
          {loading && (
            <div className="avatar-upload__loading">
              <Spinner color="#ffffff" scale="2" />
            </div>
          )}
          {!loading && <div className="avatar-upload__text">{t.component.avatarUpload.dragAndDrop}</div>}
        </div>
        <input
          type="file"
          ref={avatarUpload}
          className="avatar-upload__input"
          accept="image/*"
          onChange={(e) => {
            if (e.target.files) {
              handleOnChange(e.target.files[0]);
            }
          }}
        />

        {image && !loading && (
          <a className="avatar-upload__remove-button" onClick={() => onChange && onChange(undefined)}>
            {t.actions.remove('')}
          </a>
        )}
      </div>
      {(label || error) && (
        <footer className="avatar-upload__footer">
          {label && <div className="avatar-upload__label">{label}</div>}
          {error && <div className="avatar-upload__error">{t.errors.uploadImage}</div>}
        </footer>
      )}
    </div>
  );
}
